import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Briefcase, MapPin, Send } from "lucide-react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";
import { useJob } from "@/data/jobsApi";

export default function JobDetailPage() {
  const { slug } = useParams();
  const { job, loading, notFound } = useJob(slug);

  if (notFound) {
    // Bounce silently to the careers list - that's the closest valid page.
    return <Navigate to="/careers" replace />;
  }

  if (loading || !job) {
    return (
      <div data-testid="job-detail-loading" className="pt-32 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-4 w-32 bg-slate-200 rounded mb-6" />
          <div className="h-10 w-3/4 bg-slate-200 rounded mb-3" />
          <div className="h-5 w-1/2 bg-slate-200 rounded mb-8" />
          <div className="h-4 w-full bg-slate-200 rounded mb-2" />
          <div className="h-4 w-5/6 bg-slate-200 rounded mb-2" />
          <div className="h-4 w-4/6 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  const cleanBody = DOMPurify.sanitize(job.body_html || "");

  return (
    <div data-testid="job-detail-page" className="page-transition">
      <Seo
        title={`${job.title} - Careers at Antera Software`}
        description={job.summary || `Apply for the ${job.title} role at Antera Software in ${job.location || "Plano, TX"}.`}
        keywords={`antera careers, ${job.title}, ${job.department || ""} jobs, plano tx jobs`}
        path={`/careers/${job.slug}`}
      />

      {/* Header */}
      <section className="pt-32 pb-10 lg:pt-40 lg:pb-12 bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/careers"
            data-testid="job-back-link"
            className="inline-flex items-center text-sm text-sky-600 hover:text-sky-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> All Open Positions
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              {job.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-slate-500">
              {job.department && (
                <span className="flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4" /> {job.department}
                </span>
              )}
              {job.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {job.location}
                </span>
              )}
              {job.type && (
                <span className="bg-sky-50 text-sky-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {job.type}
                </span>
              )}
            </div>
            {job.summary && (
              <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
                {job.summary}
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={`/careers?position=${encodeURIComponent(job.title)}#apply`}>
                <Button
                  data-testid="job-detail-apply-top"
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-11 px-7 text-sm font-semibold"
                >
                  <Send className="mr-2 h-4 w-4" /> Apply for this role
                </Button>
              </Link>
              <Link to="/careers">
                <Button
                  variant="outline"
                  className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 h-11 px-6 text-sm font-semibold"
                >
                  See all openings
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article
            data-testid="job-detail-body"
            className="blog-article-content"
            dangerouslySetInnerHTML={{ __html: cleanBody }}
          />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Ready to join us?
          </h2>
          <p className="mt-3 text-sm text-slate-500">
            Hit "Apply" below and we'll prefill this role on the application form.
          </p>
          <div className="mt-6">
            <Link to={`/careers?position=${encodeURIComponent(job.title)}#apply`}>
              <Button
                data-testid="job-detail-apply-bottom"
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-12 px-8 text-sm font-semibold"
              >
                Apply for {job.title} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
